import { Typography } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableActionTD,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { Exporter, ExporterStatus, ExporterType } from 'dashboard/models/exporter';
import { User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import IconFile from 'images/icons/file.svg';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';

const StyledTableTH = styled(TableTH)`
  ${Typography.TextMediumBold};
`;

const ExportersTable = styled('div')`
  margin-bottom: ${SPACINGS.base}px;
`;

interface DashboardExportersViewProps {
  exporters: Exporter[];
  pageSize: number;
  isLoadingExporters: boolean;
  exporterPaginationParams: PaginationParams;
  productFeatureIsDisabled: boolean;
  users: User[];
  handleArchive: (exporter: Exporter) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
}

const DashboardExportersView = ({
  exporters,
  users,
  pageSize,
  isLoadingExporters,
  exporterPaginationParams,
  handleArchive,
  handlePageSizeClick,
  handlePageLinkClick,
  productFeatureIsDisabled,
}: DashboardExportersViewProps) => {
  const usersById = useMemo(() => {
    return users.reduce(
      (map, user: User) => {
        map[user.id] = user;
        return map;
      },
      {} as Record<string, User>,
    );
  }, [users]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductFeature
        title='Exporters'
        prompt='Manage and download Exporters.'
        helpLink={
          <a
            // TODO: Fix link to exporters
            href='https://support.withpersona.com/hc/en-us/articles/360057452513-Security-Privacy-and-Compliance-Overview'
            target='_blank'
            rel='noreferrer'
          >
            Learn more about exporters
          </a>
        }
        icon={<IconFile />}
        popupMessage='Your current plan does not have access to this feature.'
      />
    );
  }

  const exporterRows = exporters.map((exporter, key) => {
    return (
      <ExporterTableRow
        exporter={exporter}
        key={key}
        usersById={usersById}
        handleArchive={handleArchive}
      />
    );
  });
  return (
    <Container>
      <Header data-test='exporters__header' title='Exports' />
      <Section>
        <Card>
          <Card.Body>
            <Card.Title>Exports</Card.Title>
            <ExportersTable data-test='exporters-table'>
              <TableLayout
                dataTest='exporters-table__table'
                isEmpty={exporters.length === 0}
                isLoading={isLoadingExporters}
                showHeader={false}
                headerTR={
                  <>
                    <StyledTableTH
                      id='exports-table__header--type'
                      data-test='exports-table__header--type'
                    >
                      Type
                    </StyledTableTH>
                    <StyledTableTH
                      id='exports-table__header--type'
                      data-test='exports-table__header--type'
                    >
                      Exporter ID
                    </StyledTableTH>
                    <StyledTableTH
                      id='exports-table__header--creator'
                      data-test='exports-table__header--creator'
                    >
                      Creator
                    </StyledTableTH>
                    <StyledTableTH
                      id='exports-table__header--status'
                      data-test='exports-table__header--status'
                    >
                      Status
                    </StyledTableTH>
                    <StyledTableTH
                      id='exports-table__header--completed-at'
                      data-test='exports-table__header--completed-at'
                    >
                      {`Completed At (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
                    </StyledTableTH>
                    {/* Download Button */}
                    <StyledTableTH />
                    {/* Archive Button */}
                    <StyledTableTH />
                  </>
                }
                nullState={
                  <TableNullState hasUsedFilters={false} dataTestBlock='exporters-view'>
                    Looks like there aren&#39;t any exports yet.
                  </TableNullState>
                }
              >
                {exporterRows}
              </TableLayout>
            </ExportersTable>
            {exporters.length !== 0 && (
              <Pagination
                pageSize={pageSize}
                paginationParams={exporterPaginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardExportersView;

interface ExportersTableRowProps {
  exporter: Exporter;
  usersById: Record<string, User>;
  handleArchive: (exporter: Exporter) => void;
}

const ExporterTableRow = ({ exporter, usersById, handleArchive }: ExportersTableRowProps) => {
  const [showArchiveModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleDownloadClick = useCallback(() => {
    if (exporter.attributes.status === 'ready') {
      window.open(exporter.attributes['file-download-url'], '_self');
    }
  }, [exporter]);

  const handleArchiveClick = useCallback(() => {
    prompt(() => handleArchive(exporter));
  }, [handleArchive, exporter]);

  const completedAt =
    exporter.attributes['completed-at'] != null
      ? DateTime.fromISO(exporter.attributes['completed-at']).toFormat(DATE_AND_TIME_FORMAT)
      : '-';
  const typeToName = (type: ExporterType) => {
    switch (type) {
      case ApiModel.ExporterInquiry:
        return 'Inquiry';
      case ApiModel.ExporterAccount:
        return 'Account';
      case ApiModel.ExporterCase:
        return 'Case';
      case ApiModel.ExporterCustomFile:
        return exporter.attributes['filename'] || 'Custom Export';
      case ApiModel.ExporterInsightQuery:
        return 'Insight Query';
      case ApiModel.ExporterDataset:
        return 'Dataset';
      case ApiModel.ExporterGraph:
        return 'Graph';
      case ApiModel.ExporterReviewQueueTask:
        return 'Queue';
      case ApiModel.ExporterMetricsVerificationVolume:
        return 'Verification Analytics - Volume';
      case ApiModel.ExporterMetricsInquiry:
        return 'Inquiry Analytics - Overview';
      case ApiModel.ExporterMetricsVerificationChecks:
        return 'Verification Analytics - Checks';
      case ApiModel.ExporterReport:
        return 'Report';
      case ApiModel.ExporterTransaction:
        return 'Transaction';
    }
  };

  const user = usersById[exporter.relationships.user?.data?.id];

  let userNameOrEmail = '-';
  if (user != null && (user.attributes['name-first'] || user.attributes['name-last'])) {
    userNameOrEmail = `${user.attributes['name-first']} ${user.attributes['name-last']}`;
  } else if (user != null && user.attributes['email-address']) {
    userNameOrEmail = user.attributes['email-address'];
  }

  return (
    <>
      <ModalWrapper isOpen={showArchiveModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Archive this export?'
          confirmActionText='Archive export'
        >
          <p>This will delete all prepared files within this export. Are you sure?</p>
        </ConfirmationModal>
      </ModalWrapper>
      <TableTR>
        <TableTD data-test='exports-table__type'>{typeToName(exporter.type)}</TableTD>
        <TableTD>{exporter.id}</TableTD>
        <TableTD data-test='exports-table__name'>{userNameOrEmail}</TableTD>
        <TableTD>{exporter.attributes.status}</TableTD>
        <TableTD>{completedAt}</TableTD>
        <TableActionTD>
          {exporter.attributes.status === ExporterStatus.Ready && (
            <Button
              onClick={handleDownloadClick}
              id={`exports-table__${exporter.id}`}
              data-test={`exports-table__${exporter.id}`}
              variant='basic'
            >
              Download
            </Button>
          )}
        </TableActionTD>
        <TableActionTD>
          {exporter.attributes.status !== ExporterStatus.Archived && (
            <Button
              onClick={handleArchiveClick}
              id={`exports-table__${exporter.id}--archive`}
              data-test={`exports-table__${exporter.id}--archive`}
              variant='danger'
            >
              Archive
            </Button>
          )}
        </TableActionTD>
      </TableTR>
    </>
  );
};
